import { UseMutateFunction } from '@tanstack/react-query'
import { FC } from 'react'
import usePermissions from '../../../common/hooks/usePermissions'
import { RescueStatusEnum } from '../../../common/interfaces'
import {
  IPaymentStatusEnum,
  SolicitudeStatusEnum,
} from '../../../common/interfaces/payment.interface'
import { customSwal } from '../../../config/swal'
import { IFilterStatus } from './tabs/SuscriptionTab'

interface IStatus {
  id: string
  label?: string
  value: SolicitudeStatusEnum | IPaymentStatusEnum
}

const listStatus: IStatus[] = [
  { id: '3', label: 'Anulado', value: IPaymentStatusEnum.ANNULLED },
  { id: '2', label: 'Aprobado', value: IPaymentStatusEnum.APPROVED },
  { id: '1', label: 'Ingresar solicitud a Spectrum', value: IPaymentStatusEnum.PENDING },
]
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const listStatusV2: IStatus[] = [
  { id: '1', value: SolicitudeStatusEnum.ANULADA },
  { id: '2', value: SolicitudeStatusEnum.CONFIRMADA },
  { id: '3', value: SolicitudeStatusEnum.INGRESADA },
  { id: '4', value: SolicitudeStatusEnum.LIQUIDADA },
  { id: '5', value: SolicitudeStatusEnum.PROCESADA },
]

interface IProps {
  mutate?: UseMutateFunction<any, unknown, IFilterStatus, unknown>
  rescue?: boolean
  mutationLoading: boolean
  solicitude?: boolean
  selectedRescueState?: RescueStatusEnum | null
  setSelectedStatus: (
    state: null | IPaymentStatusEnum,
    cb?: ((state: null | IPaymentStatusEnum) => void) | undefined
  ) => void
  setComment?: (state: string, cb?: ((state: string) => void) | undefined) => void
  showAnnulled?: boolean
  permissionGuards?: {
    annul?: string | string[] // ver y ejecutar "Anulado"
    approve?: string | string[] // ver y ejecutar "Aprobado"
    spectrum?: string | string[] // ver y ejecutar "Ingresar a Spectrum" (PENDING)
  }
}

export const StatusList: FC<IProps> = ({
  mutate,
  rescue,
  mutationLoading,
  selectedRescueState,
  setSelectedStatus,
  solicitude,
  setComment,
  showAnnulled = false,
  permissionGuards,
}) => {
  const { hasPermission } = usePermissions()
  const can = (perm?: string | string[]) => {
    if (!perm) return true
    return hasPermission(perm)
  }
  // Habilitadores por acción
  const allowAnnulled = showAnnulled && can(permissionGuards?.annul)
  const allowApproved = can(permissionGuards?.approve)
  const allowSpectrum = can(permissionGuards?.spectrum)

  // Filtro por permisos + reglas existentes
  const passesPermission = (s: IStatus) => {
    if (s.value === IPaymentStatusEnum.ANNULLED) return allowAnnulled
    if (s.value === IPaymentStatusEnum.APPROVED) return allowApproved
    if (s.value === IPaymentStatusEnum.PENDING) return allowSpectrum
    return false
  }
  const handleMutation = (status?: string) => {
    customSwal
      .fire({
        title: `¿Está seguro que desea cambiar el estado de ${
          rescue ? 'los rescates' : 'las suscripciones'
        } a <strong>${listStatus.find((e) => e.value === status)?.label}</strong>?`,
        icon: 'warning',
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: `Sí, cambiar`,
        cancelButtonText: 'Cancelar',
      })
      .then((result) => {
        if (result.isConfirmed) {
          mutate && mutate(status as IFilterStatus)
        }
      })
  }

  const getFilteredStatus = (status: IStatus) => {
    if (rescue) {
      if (selectedRescueState && selectedRescueState === RescueStatusEnum.PENDING) {
        return status.value !== IPaymentStatusEnum.APPROVED
      }
      return status.value !== IPaymentStatusEnum.PENDING
    }
    if (!solicitude) {
      return status.value !== IPaymentStatusEnum.PENDING
    }
    return status.value === IPaymentStatusEnum.ANNULLED
  }
  const extraContextRules = (status: IStatus) => {
    // ✅ Reutiliza tus reglas existentes:
    if (rescue) {
      if (selectedRescueState && selectedRescueState === RescueStatusEnum.PENDING) {
        // En PENDING no se muestra "Aprobado"
        return status.value !== IPaymentStatusEnum.APPROVED
      }
      // En otros estados no se muestra "Ingresar a Spectrum"
      return status.value !== IPaymentStatusEnum.PENDING
    }
    if (!solicitude) {
      // En suscripciones (no pendientes) no se muestra Spectrum
      return status.value !== IPaymentStatusEnum.PENDING
    }
    // En solicitudes (pendientes) solo queremos Anulado (y Spectrum si se permite)
    return (
      status.value === IPaymentStatusEnum.ANNULLED || status.value === IPaymentStatusEnum.PENDING
    )
  }

  const baseFiltered = listStatus.filter(passesPermission).filter(extraContextRules)

  const handleClick = (status: IStatus) => {
    // Guardia doble (por si alguien intenta disparar por código)
    if (!passesPermission(status)) return

    setSelectedStatus(status.value as IPaymentStatusEnum)
    if (status.value === IPaymentStatusEnum.ANNULLED) {
      $('#comment-section').modal('toggle')
      setComment && setComment('')
    } else {
      handleMutation(status.value)
    }
  }

  return (
    <div className='d-flex gap-3' {...(selectedRescueState ? { key: selectedRescueState } : {})}>
      {baseFiltered
        .filter((statusRaw) => getFilteredStatus(statusRaw))
        .map((status) => {
          return (
            <button
              disabled={mutationLoading}
              key={status.id}
              style={{
                border: 'none',
              }}
              onClick={() => handleClick(status)}
              className='flex-grow-1 badge-light badge rounded py-2 px-3'
            >
              <div className='d-flex align-items-center '>
                <div>{status.label}</div>
              </div>
            </button>
          )
        })}
    </div>
  )
}
